import { el } from "../components/dom.js";
import { progressBar } from "../components/tracking-fields.js";
import { progressPct, progressLabel } from "../utils/tracking.js";
import { listHistory } from "../db/history.js";
import { onThisDay, onThisDayLine, staleBacklog, loadSnoozed } from "../utils/insights.js";
import { todayDate } from "../utils/dates.js";
import { cachedNews } from "../api/news.js";
import { issueNumber } from "../utils/news.js";
import { onSwipe } from "../utils/swipe.js";
import { listGroups, subtreeMembers } from "../db/groups.js";
import { listAllMedia, updateMediaStatus } from "../db/media.js";

import { upcoming } from "../utils/release.js";
import { buildSuggestions } from "../db/recommendations.js";
import { buildAiring, dedupeById } from "../db/airing.js";
import { listSubscriptions } from "../db/comic-subscriptions.js";
import { scheduleRows } from "../utils/comic-subscriptions.js";
import { setEpisodeSeen as tvSetEpisodeSeen } from "../db/tv.js";
import { setEpisodeSeen as animeSetEpisodeSeen } from "../db/anime.js";
import { magGlass, magCover, magButton } from "../components/magazine/kit.js";
import { formatReleaseDate } from "../utils/dates.js";

import {
	MAG,
	MAG_CAT,
	MAG_CAT_ORDER,
	magCat,
	magTileBg,
	magStickerAbsolute,
	magSectionHead,
} from "../utils/magazine.js";
import { navigate } from "../main.js";


// Home: the magazine cover hero + editorial mosaic (the old "calm" vertical
// list went with the calm skin).
export async function renderHome(root) {
	return renderHomeMagazine(root);
}

// ======================================================================
// MAGAZINE Home (Tier 2) — cover hero + stat blocks + mosaic + collections.
// Shared editorial building blocks live in utils/magazine.js.
// ======================================================================

async function renderHomeMagazine(root) {
	const view = el("div.view", { style: { padding: `var(--topbar-h) 12px env(safe-area-inset-bottom,16px)` } });
	root.append(view);
	// Swipe right → left to open the Newsstand, like turning the page.
	onSwipe(view, { left: () => navigate("/news") });

	function rerender() {
		root.innerHTML = "";
		renderHome(root);
	}

	const allMedia = await listAllMedia();
	const active = allMedia.filter((m) => m.engagement_status === "playing");
	const [{ recommendations, upcomingSeasons }, airing, subscriptions] = await Promise.all([
		buildSuggestions(),
		buildAiring(),
		listSubscriptions(),
	]);

	// Editorial masthead strip under the persistent top bar.
	view.append(
		el(
			"div",
			{ style: { display: "flex", alignItems: "center", gap: "8px", padding: "10px 4px 4px" } },
			el("span", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "9.5px",
					letterSpacing: ".16em", textTransform: "uppercase", color: MAG.lime,
				},
			}, "The Collection"),
			el("span", {
				style: { flex: "1", height: "2px", background: `linear-gradient(90deg, ${MAG.lime}b3, ${MAG.lime}00)` },
			}),
			el("span", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "9.5px",
					letterSpacing: ".12em", textTransform: "uppercase", color: MAG.dim,
				},
			}, `Issue Nº${allMedia.length}`),
		),
	);

	// First run (roadmap #69): a welcome card until it's dismissed or the
	// library has a few things in it.
	if (showOnboarding(allMedia)) view.append(magWelcome(rerender));

	// Cover hero — the first in-progress item.
	if (active[0]) view.append(magHero(active[0], rerender));
	else view.append(magHeroEmpty());

	// Category stat blocks.
	view.append(magStatBlocks(allMedia));

	// "On this day" — what you did on today's date in earlier years (History).
	const memories = onThisDay(await listHistory(), todayDate());
	if (memories.length) view.append(magOnThisDay(memories));

	// "Also on the go" — the rest of the in-progress items as a mosaic.
	const rest = active.slice(1);
	if (rest.length) {
		view.append(magSectionHead("Also on the go", MAG.yellow));
		view.append(magMosaic(rest));
	}

	// "Continue watching" — the next aired-but-unseen episode of each series you're
	// Watching, with a one-tap Seen. Hidden when you're caught up everywhere.
	if (airing.continueWatching.length) {
		view.append(magContinueWatching(airing.continueWatching));
	}

	// Incoming release schedule strip: dated games/books, upcoming TV/anime seasons
	// and the next episode of each airing series (deduped — the recommendation
	// engine and the airing tracker can both produce a season row).
	const upcomingCount = upcoming(
		dedupeById([
			...allMedia,
			...upcomingSeasons,
			...airing.upcomingSeasons,
			...airing.upcomingEpisodes,
			...scheduleRows(subscriptions),
		]),
	).length;
	if (upcomingCount > 0) {
		view.append(magIncoming(upcomingCount));
	}

	// Today's issue of the Newsstand — the lead headline from the last saved issue
	// (no network on Home; the Newsstand fetches when opened).
	view.append(magNewsstandStrip());

	// Recommendations strip — what to engage with next.
	if (recommendations.length > 0) {
		view.append(magRecommendations(recommendations.length));
	}

	// Backlog that has waited six months or more — "Still want these?".
	const stale = staleBacklog(allMedia, { snoozed: loadSnoozed() });
	if (stale.length) view.append(magBacklogLink(stale));

	// History — your activity log, past releases and the notification archive.
	view.append(magHistoryLink(), magYearLink());
	view.append(magPeopleLink());

	// Collections (pinned groups + an "all groups" link).
	const groups = await listGroups();
	view.append(magCollections(groups, allMedia));

	view.append(
		el("div", {
			style: {
				textAlign: "center", fontFamily: "'Archivo', sans-serif", fontWeight: "900",
				fontSize: "9px", letterSpacing: ".22em", textTransform: "uppercase",
				color: "#46464c", padding: "24px 0 26px",
			},
		}, `MediaTracker · v${__APP_VERSION__}`),
	);
}

function magHero(item, rerender) {
	const cat = magCat(item.media_type);

	async function setStatus(value) {
		await updateMediaStatus(item, value);
		rerender();
	}

	const ratingCorner = item.rating != null
		? el(
				"div",
				{ style: { position: "absolute", top: "14px", right: "14px", textAlign: "right", transform: "rotate(3deg)" } },
				el("div", {
					style: { fontFamily: "'Anton', sans-serif", fontSize: "26px", color: MAG.yellow, lineHeight: ".85", textShadow: "0 2px 10px rgba(0,0,0,.5)" },
				}, String(item.rating)),
				el("div", {
					style: { fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "8px", letterSpacing: ".14em", textTransform: "uppercase", color: "#fff" },
				}, "/ 5"),
			)
		: null;

	const footer = el(
		"div",
		{
			style: {
				position: "absolute", left: "10px", right: "10px", bottom: "10px",
				display: "flex", alignItems: "center", gap: "10px", padding: "11px 12px",
				borderRadius: "13px", background: "rgba(14,12,20,.42)",
				backdropFilter: "blur(18px) saturate(1.5)", webkitBackdropFilter: "blur(18px) saturate(1.5)",
				border: `1px solid ${MAG.line}`, boxShadow: "0 8px 28px rgba(0,0,0,.4)",
			},
		},
		el(
			"div",
			{ style: { flex: "1", minWidth: "0" } },
			el("div", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "8.5px",
					letterSpacing: ".1em", textTransform: "uppercase", color: MAG.lime,
					overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
				},
			}, item.secondary ? `${cat.verb} · ${item.secondary}` : cat.verb),
			item.progress
				? el("div", { style: { marginTop: "6px" } },
					progressBar(item.progress) || "",
					el("div", { style: { fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "10px", color: "#fff", marginTop: "4px" } }, progressLabel(item.progress)))
				: null,
		),
		el("button", {
			type: "button",
			onclick: () => setStatus("done"),
			style: {
				fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "10px", letterSpacing: ".06em",
				textTransform: "uppercase", color: "#0b0b0f", background: MAG.lime, border: "0",
				borderRadius: "999px", padding: "8px 14px", cursor: "pointer",
			},
		}, "Done"),
		el("button", {
			type: "button",
			onclick: () => setStatus("on_pause"),
			style: {
				fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "10px", letterSpacing: ".06em",
				textTransform: "uppercase", color: "#fff", background: "rgba(255,255,255,.14)",
				border: `1px solid ${MAG.line}`, borderRadius: "999px", padding: "8px 13px", cursor: "pointer",
			},
		}, "Pause"),
	);

	return el(
		"div",
		{
			role: "button",
			onclick: (e) => { if (e.target.tagName !== "BUTTON") navigate(item.route); },
			style: {
				position: "relative", height: "clamp(280px, 64vw, 340px)", margin: "8px 0 0",
				borderRadius: "16px", overflow: "hidden", cursor: "pointer",
				boxShadow: "0 14px 36px rgba(0,0,0,.55)", background: magTileBg(item),
			},
		},
		magStickerAbsolute("▶ Now Playing", { top: "14px", left: "14px", rotate: "-2deg" }),
		ratingCorner,
		el(
			"div",
			{ style: { position: "absolute", left: "16px", right: "16px", bottom: "92px" } },
			el("div", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "10px",
					letterSpacing: ".14em", textTransform: "uppercase", color: cat.color === "#8b3dff" ? "#19d3e8" : MAG.lime, marginBottom: "2px",
				},
			}, item.secondary ? `${cat.label} · ${item.secondary}` : cat.label),
			el("div", {
				style: {
					fontFamily: "'Anton', sans-serif", fontSize: "clamp(36px, 13vw, 56px)",
					lineHeight: ".82", textTransform: "uppercase", color: "#fff",
					textShadow: "0 3px 18px rgba(0,0,0,.45)",
				},
			}, item.title),
		),
		footer,
	);
}

function magHeroEmpty() {
	return el(
		"div",
		{
			style: {
				position: "relative", height: "clamp(200px, 44vw, 240px)", margin: "8px 0 0",
				borderRadius: "16px", overflow: "hidden", display: "flex", flexDirection: "column",
				alignItems: "center", justifyContent: "center", gap: "10px",
				background: "radial-gradient(rgba(255,255,255,.08) 1.2px,transparent 1.5px) 0 0/9px 9px, linear-gradient(150deg,#1d1d25,#0b0b0f)",
				border: `1px solid ${MAG.line}`,
			},
		},
		el("div", { style: { fontFamily: "'Anton', sans-serif", fontSize: "30px", textTransform: "uppercase", color: "#fff" } }, "Nothing in play"),
		el("div", {
			style: { fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "10px", letterSpacing: ".08em", textTransform: "uppercase", color: MAG.dim },
		}, "Mark something Playing or Reading"),
	);
}

function magStatBlocks(allMedia) {
	const row = el("div", { style: { display: "flex", gap: "7px", padding: "14px 0 4px" } });
	for (const type of MAG_CAT_ORDER) {
		const cat = MAG_CAT[type];
		const n = allMedia.filter((m) => m.media_type === type).length;
		const onLight = type === "tv"; // cyan needs dark text
		row.append(
			el(
				"button",
				{
					type: "button",
					onclick: () => navigate(cat.route),
					style: { flex: "1", border: "0", cursor: "pointer", borderRadius: "11px", padding: "10px 9px", background: cat.color, textAlign: "left" },
				},
				el("div", { style: { fontFamily: "'Anton', sans-serif", fontSize: "22px", color: onLight ? "#06222a" : "#fff", lineHeight: ".9" } }, String(n)),
				el("div", {
					style: {
						fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "8px", letterSpacing: ".1em",
						textTransform: "uppercase", color: onLight ? "#06343f" : "rgba(255,255,255,.88)", marginTop: "3px",
					},
				}, cat.count),
			),
		);
	}
	return row;
}

function magTile(item, height) {
	const cat = magCat(item.media_type);
	return el(
		"div",
		{
			role: "button",
			onclick: () => navigate(item.route),
			style: {
				position: "relative", height, borderRadius: "14px", overflow: "hidden", cursor: "pointer",
				boxShadow: "0 8px 22px rgba(0,0,0,.4)", background: magTileBg(item),
			},
		},
		magStickerAbsolute(cat.verb, { top: "10px", left: "10px", bg: "#fff" }),
		el("div", {
			style: {
				position: "absolute", left: "11px", right: "11px", bottom: "10px",
				fontFamily: "'Anton', sans-serif", fontSize: parseInt(height, 10) > 150 ? "26px" : "20px",
				lineHeight: ".86", textTransform: "uppercase", color: "#fff", textShadow: "0 2px 12px rgba(0,0,0,.45)",
			},
		}, item.title),
		// Progress (roadmap #28): a thin bar along the bottom edge.
		progressPct(item.progress) != null
			? el("div", { style: { position: "absolute", left: "0", right: "0", bottom: "0" } }, progressBar(item.progress, { height: "4px" }))
			: null,
	);
}

function magMosaic(rest) {
	const wrap = el("div", {});
	if (rest.length === 1) {
		wrap.append(magTile(rest[0], "150px"));
		return wrap;
	}
	const left = magTile(rest[0], "226px");
	const rightChildren = [magTile(rest[1], "109px")];

	// Items beyond the first two are revealed in a grid below when the "+N more"
	// tile is tapped (it used to be a dead decorative placeholder).
	const hidden = rest.slice(2);
	if (hidden.length > 0) {
		const moreTile = el(
			"div",
			{
				role: "button",
				onclick: () => {
					moreTile.remove();
					wrap.append(
						el(
							"div",
							{
								style: {
									display: "grid", gridTemplateColumns: "repeat(2, 1fr)",
									gap: "8px", marginTop: "8px",
								},
							},
							...hidden.map((item) => magTile(item, "109px")),
						),
					);
				},
				style: {
					height: "109px", borderRadius: "14px", display: "flex", flexDirection: "column",
					alignItems: "center", justifyContent: "center", gap: "6px", background: MAG.bgGlass,
					backdropFilter: "blur(14px)", webkitBackdropFilter: "blur(14px)", border: `1px solid ${MAG.line}`,
					cursor: "pointer",
				},
			},
			el("div", { style: { fontFamily: "'Anton', sans-serif", fontSize: "30px", color: "#fff", lineHeight: ".8" } }, `+${hidden.length}`),
			el("div", {
				style: { fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "8px", letterSpacing: ".1em", textTransform: "uppercase", color: MAG.lime },
			}, "More in play"),
		);
		rightChildren.push(moreTile);
	}
	wrap.append(
		el(
			"div",
			{ style: { display: "flex", gap: "8px" } },
			el("div", { style: { flex: "1" } }, left),
			el("div", { style: { flex: "1", display: "flex", flexDirection: "column", gap: "8px" } }, ...rightChildren),
		),
	);
	return wrap;
}

// "Continue watching": section head + one glass row per series you're Watching
// (cover, "S2E6 · aired 12 Sep · +2 more", the series title, the episode name) with
// a trailing Seen button. Seen marks the episode and re-derives the list in
// place, so the row advances to the next episode — or the section folds away —
// without re-rendering Home.
function magContinueWatching(rows) {
	const section = el("div", {});
	const list = el("div", { style: { display: "flex", flexDirection: "column", gap: "9px" } });
	section.append(magSectionHead("Continue watching", MAG.lime), list);

	const render = (entries) => {
		if (entries.length === 0) {
			section.remove();
			return;
		}
		list.replaceChildren(...entries.map(rowEl));
	};

	function rowEl(entry) {
		const { series, mediaType, episode, unseen, item } = entry;
		const kicker = [
			item.label,
			episode.airdate ? `aired ${formatReleaseDate(episode.airdate)}` : null,
			unseen > 1 ? `+${unseen - 1} more` : null,
		].filter(Boolean).join(" · ");
		const markSeen = async (e) => {
			e?.stopPropagation?.();
			const setSeen = mediaType === "anime" ? animeSetEpisodeSeen : tvSetEpisodeSeen;
			await setSeen(series.id, episode.season, episode.number, true);
			render((await buildAiring()).continueWatching);
		};
		const body = el(
			"div",
			{ style: { flex: "1", minWidth: "0" } },
			el("div", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "9px",
					letterSpacing: ".06em", textTransform: "uppercase", color: MAG.dim, marginBottom: "3px",
				},
			}, kicker),
			el("div", {
				style: {
					fontFamily: "'Anton', sans-serif", fontSize: "18px", lineHeight: ".95",
					textTransform: "uppercase", color: "#fff",
					overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
				},
			}, series.title ?? ""),
			episode.name
				? el("div", {
					style: {
						fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "11px",
						color: MAG.dim, marginTop: "3px",
						overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
					},
				}, episode.name)
				: null,
		);
		return magGlass(
			[
				magCover(
					{ title: series.title, cover_url: item.cover_url, media_type: mediaType },
					{ height: "62px", width: "46px", radius: "8px" },
				),
				body,
				magButton("Seen", { variant: "primary", onClick: markSeen }),
			],
			{
				role: "button",
				onClick: () => navigate(item.route),
				style: { display: "flex", alignItems: "center", gap: "12px", padding: "10px 12px" },
			},
		);
	}

	render(rows);
	return section;
}

function magIncoming(count) {
	return el(
		"div",
		{
			role: "button",
			onclick: () => navigate("/upcoming"),
			style: {
				margin: "16px 0 0", borderRadius: "14px", padding: "13px 14px", cursor: "pointer",
				display: "flex", alignItems: "center", gap: "12px",
				background: "linear-gradient(100deg,#2f6bff,#8b3dff)", boxShadow: "0 8px 22px rgba(47,107,255,.3)",
			},
		},
		el("div", { style: { fontFamily: "'Anton', sans-serif", fontSize: "34px", color: MAG.yellow, lineHeight: ".85" } }, String(count)),
		el(
			"div",
			{ style: { flex: "1" } },
			el("div", { style: { fontFamily: "'Anton', sans-serif", fontSize: "17px", textTransform: "uppercase", color: "#fff", lineHeight: ".9" } }, "Incoming"),
			el("div", {
				style: { fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "9.5px", letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(255,255,255,.85)", marginTop: "2px" },
			}, "Release schedule"),
		),
		el("span", { style: { fontFamily: "'Anton', sans-serif", fontSize: "24px", color: "#fff" } }, "›"),
	);
}

// Sibling of magIncoming, in the lime/poppy palette, linking to the full
// Recommendations list.
function magRecommendations(count) {
	return el(
		"div",
		{
			role: "button",
			onclick: () => navigate("/recommendations"),
			style: {
				margin: "10px 0 0", borderRadius: "14px", padding: "13px 14px", cursor: "pointer",
				display: "flex", alignItems: "center", gap: "12px",
				background: `linear-gradient(100deg,#1f8f3a,${MAG.lime})`, boxShadow: "0 8px 22px rgba(123,211,26,.28)",
			},
		},
		el("div", { style: { fontFamily: "'Anton', sans-serif", fontSize: "34px", color: "#0b0b0f", lineHeight: ".85" } }, String(count)),
		el(
			"div",
			{ style: { flex: "1" } },
			el("div", { style: { fontFamily: "'Anton', sans-serif", fontSize: "17px", textTransform: "uppercase", color: "#0b0b0f", lineHeight: ".9" } }, "Recommendations"),
			el("div", {
				style: { fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "9.5px", letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(11,11,15,.7)", marginTop: "2px" },
			}, "Worth a look next"),
		),
		el("span", { style: { fontFamily: "'Anton', sans-serif", fontSize: "24px", color: "#0b0b0f" } }, "›"),
	);
}

// The Newsstand's door: loud yellow with a checkerboard edge, echoing its
// 90s look, showing today's issue number and the last saved lead headline.
function magNewsstandStrip() {
	const last = cachedNews();
	const lead = last?.items?.find((s) => s.image) ?? last?.items?.[0] ?? null;
	return el(
		"div",
		{
			role: "button",
			onclick: () => navigate("/news"),
			style: {
				margin: "10px 0 0", borderRadius: "4px", cursor: "pointer", overflow: "hidden",
				background: "#ffe600", color: "#0b0b0f", boxShadow: "6px 6px 0 #ff2e88", border: "3px solid #0b0b0f",
			},
		},
		el("div", { style: { height: "8px", background: "repeating-conic-gradient(#0b0b0f 0% 25%, #ffe600 0% 50%) 0 0 / 8px 8px" } }),
		el(
			"div",
			{ style: { display: "flex", alignItems: "center", gap: "12px", padding: "10px 12px" } },
			el(
				"div",
				{ style: { flex: "1", minWidth: "0" } },
				el("div", { style: { fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "9.5px", letterSpacing: ".12em" } },
					`NEWSSTAND · ISSUE Nº ${issueNumber(todayDate())} · OR SWIPE ‹`),
				el("div", {
					style: {
						fontFamily: "'Anton', sans-serif", fontSize: "17px", textTransform: "uppercase", lineHeight: "1.05", marginTop: "3px",
						overflow: "hidden", display: "-webkit-box", webkitLineClamp: "2", webkitBoxOrient: "vertical",
					},
				}, lead ? lead.title : "Hot off the wire — open today's issue"),
			),
			el("span", { style: { fontFamily: "'Anton', sans-serif", fontSize: "24px" } }, "›"),
		),
	);
}

// "On this day": up to three memories, each opening its item.
function magOnThisDay(memories) {
	return el(
		"div",
		{ style: { marginTop: "14px" } },
		magSectionHead("On this day", "#19d3e8"),
		...memories.slice(0, 3).map((m) =>
			magGlass(
				[
					magCover({ cover_url: m.event.cover_url, media_type: m.event.media_type }, { height: "52px", width: "38px", radius: "6px" }),
					el("div", { style: { flex: "1", minWidth: "0" } },
						el("div", { style: { fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "9.5px", letterSpacing: ".1em", textTransform: "uppercase", color: MAG.dim } }, onThisDayLine(m)),
						el("div", { style: { fontFamily: "'Anton', sans-serif", fontSize: "17px", textTransform: "uppercase", color: "#fff", lineHeight: "1.05", marginTop: "2px" } }, m.event.title ?? "")),
				],
				{
					role: "button",
					onClick: () => m.event.route && navigate(m.event.route),
					style: { display: "flex", alignItems: "center", gap: "12px", padding: "10px 12px", marginTop: "8px" },
				},
			),
		),
	);
}

// The backlog that has waited longest — a quiet row into /backlog.
function magBacklogLink(stale) {
	const oldest = stale[0];
	return el(
		"div",
		{
			role: "button",
			onclick: () => navigate("/backlog"),
			style: {
				margin: "10px 0 0", borderRadius: "14px", padding: "12px 14px", cursor: "pointer",
				display: "flex", alignItems: "center", gap: "12px", background: MAG.bgGlass, border: `1px solid ${MAG.line}`,
			},
		},
		el("div", { style: { fontFamily: "'Anton', sans-serif", fontSize: "30px", color: MAG.yellow, lineHeight: ".85" } }, String(stale.length)),
		el("div", { style: { flex: "1", minWidth: "0" } },
			el("div", { style: { fontFamily: "'Anton', sans-serif", fontSize: "17px", textTransform: "uppercase", color: "#fff", lineHeight: ".9" } }, "Still want these?"),
			el("div", {
				style: { fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "9.5px", letterSpacing: ".08em", textTransform: "uppercase", color: MAG.dim, marginTop: "3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
			}, `Oldest: ${oldest.item.title} · ${oldest.days} days`)),
		el("span", { style: { fontFamily: "'Anton', sans-serif", fontSize: "24px", color: "#fff" } }, "›"),
	);
}

// A quiet glass row (not a poppy strip like Incoming): History is a look back,
// not a call to action.
function magHistoryLink() {
	return el(
		"div",
		{
			role: "button",
			onclick: () => navigate("/history"),
			style: {
				margin: "10px 0 0", borderRadius: "14px", padding: "12px 14px", cursor: "pointer",
				display: "flex", alignItems: "center", gap: "12px",
				background: MAG.bgGlass, border: `1px solid ${MAG.line}`,
			},
		},
		el("span", { style: { fontSize: "20px", flex: "none" } }, "🕘"),
		el(
			"div",
			{ style: { flex: "1" } },
			el("div", { style: { fontFamily: "'Anton', sans-serif", fontSize: "17px", textTransform: "uppercase", color: "#fff", lineHeight: ".9" } }, "History"),
			el("div", {
				style: { fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "9.5px", letterSpacing: ".08em", textTransform: "uppercase", color: MAG.dim, marginTop: "2px" },
			}, "What you did · past releases · notifications"),
		),
		el("span", { style: { fontFamily: "'Anton', sans-serif", fontSize: "24px", color: "#fff" } }, "›"),
	);
}

// Year in review + month by month (roadmap #24, #25), same glass row.
function magYearLink() {
	const link = magHistoryLink();
	link.onclick = () => navigate("/year");
	link.children[0].textContent = "📅";
	link.children[1].children[0].textContent = "Year in review";
	link.children[1].children[1].textContent = "Your year · month by month · diary export";
	return link;
}

// Same quiet glass row as History, for the people you follow.
function magPeopleLink() {
	return el(
		"div",
		{
			role: "button",
			onclick: () => navigate("/people"),
			style: {
				margin: "8px 0 0", borderRadius: "14px", padding: "12px 14px", cursor: "pointer",
				display: "flex", alignItems: "center", gap: "12px",
				background: MAG.bgGlass, border: `1px solid ${MAG.line}`,
			},
		},
		el("span", { style: { fontSize: "20px", flex: "none" } }, "★"),
		el(
			"div",
			{ style: { flex: "1" } },
			el("div", { style: { fontFamily: "'Anton', sans-serif", fontSize: "17px", textTransform: "uppercase", color: "#fff", lineHeight: ".9" } }, "People"),
			el("div", {
				style: { fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "9.5px", letterSpacing: ".08em", textTransform: "uppercase", color: MAG.dim, marginTop: "2px" },
			}, "Directors · showrunners · authors · creators you follow"),
		),
		el("span", { style: { fontFamily: "'Anton', sans-serif", fontSize: "24px", color: "#fff" } }, "›"),
	);
}

function magCollections(groups, allMedia) {
	const wrap = el("div", {});
	wrap.append(magSectionHead("Collections", "#19d3e8"));

	const pinned = groups.filter((g) => g.pinned_to_home);
	const spineColors = ["#8b3dff", "#ff7a1a", "#19d3e8", "#ff3d8b"];

	for (const group of pinned) {
		const members = subtreeMembers(group, groups, allMedia);
		const spines = el("div", { style: { display: "flex", flex: "none" } });
		spineColors.slice(0, 3).forEach((c, i) => {
			spines.append(
				el("div", {
					style: {
						width: "30px", height: "42px", borderRadius: "5px",
						background: `linear-gradient(150deg, ${c}, #0b0b0f)`,
						marginLeft: i === 0 ? "0" : "-12px", boxShadow: "0 2px 6px rgba(0,0,0,.4)",
					},
				}),
			);
		});
		wrap.append(
			el(
				"div",
				{
					role: "button",
					onclick: () => navigate(`/groups/${group.id}`),
					style: {
						display: "flex", gap: "11px", alignItems: "center", padding: "11px", marginBottom: "8px",
						borderRadius: "14px", cursor: "pointer", background: MAG.bgGlass,
						backdropFilter: "blur(14px)", webkitBackdropFilter: "blur(14px)", border: `1px solid ${MAG.line}`,
					},
				},
				spines,
				el(
					"div",
					{ style: { flex: "1", minWidth: "0" } },
					el("div", {
						style: { fontFamily: "'Anton', sans-serif", fontSize: "18px", textTransform: "uppercase", color: "#fff", lineHeight: ".9", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
					}, group.name),
					el("div", {
						style: { fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "9px", letterSpacing: ".06em", textTransform: "uppercase", color: MAG.dim, marginTop: "3px" },
					}, `${members.length} item${members.length === 1 ? "" : "s"}`),
				),
				el("span", { style: { fontFamily: "'Anton', sans-serif", fontSize: "22px", color: MAG.lime } }, "›"),
			),
		);
	}

	// "All groups" link row.
	wrap.append(
		el(
			"button",
			{
				type: "button",
				onclick: () => navigate("/groups"),
				style: {
					width: "100%", textAlign: "left", display: "flex", alignItems: "center", justifyContent: "space-between",
					padding: "12px 14px", borderRadius: "14px", cursor: "pointer",
					background: "transparent", border: `1px dashed ${MAG.line}`,
				},
			},
			el("span", {
				style: { fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "12px", letterSpacing: ".04em", textTransform: "uppercase", color: MAG.dim },
			}, groups.length ? "All groups" : "Make a collection"),
			el("span", { style: { fontFamily: "'Anton', sans-serif", fontSize: "20px", color: MAG.lime } }, "›"),
		),
	);

	return wrap;
}


// ---------- first-run welcome (roadmap #69) ----------

const ONBOARDING_KEY = "onboarding:done";

function showOnboarding(allMedia) {
	if (allMedia.length >= 5) return false;
	try {
		return localStorage.getItem(ONBOARDING_KEY) !== "1";
	} catch {
		return allMedia.length === 0;
	}
}

function magWelcome(rerender) {
	const done = () => {
		try {
			localStorage.setItem(ONBOARDING_KEY, "1");
		} catch {
			// fine
		}
		rerender();
	};
	const step = (n, title, sub, ...actions) =>
		el("div", { style: { display: "flex", gap: "12px", padding: "10px 0", borderTop: n > 1 ? `1px solid ${MAG.line}` : "0" } },
			el("div", { style: { fontFamily: "'Anton', sans-serif", fontSize: "26px", color: MAG.lime, lineHeight: "1", width: "22px", flex: "none" } }, String(n)),
			el("div", { style: { flex: "1", minWidth: "0" } },
				el("div", { style: { fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "13px", color: "#fff" } }, title),
				el("div", { style: { fontFamily: "'Archivo', sans-serif", fontSize: "12px", color: MAG.dim, marginTop: "2px", lineHeight: "1.4" } }, sub),
				actions.length ? el("div", { style: { display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "8px" } }, ...actions) : ""));
	const pill = (label, route, color) =>
		el("button", {
			type: "button",
			onclick: () => navigate(route),
			style: {
				fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "10px", letterSpacing: ".05em", textTransform: "uppercase",
				borderRadius: "999px", padding: "7px 12px", cursor: "pointer", border: "0", color: "#0b0b0f", background: color ?? MAG.lime,
			},
		}, label);
	return el("div", {
		style: { margin: "10px 0 0", borderRadius: "16px", padding: "16px", background: "rgba(182,242,61,.08)", border: `1px solid rgba(182,242,61,.35)` },
	},
	el("div", { style: { fontFamily: "'Anton', sans-serif", fontSize: "30px", lineHeight: ".9", color: "#fff", textTransform: "uppercase" } }, "Welcome!"),
	el("div", { style: { fontFamily: "'Archivo', sans-serif", fontSize: "12.5px", color: "#cfcfd6", margin: "6px 0 6px", lineHeight: "1.45" } },
		"Track what you play, read and watch — all in one place, on all your devices."),
	step(1, "Add your first things", "Search a catalog and tap + to add. Mark what you're on, what you've finished, what you own.",
		pill("Game", "/games/add", "#8b3dff"), pill("Book", "/books/add", "#ff7a1a"), pill("Movie", "/movies/add", "#e5383b"),
		pill("TV", "/tv/add", "#19d3e8"), pill("Anime", "/anime/add", "#ff3d8b"), pill("Comic", "/comics/add", "#1fbf5f")),
	step(2, "Or bring your history", "Goodreads, Letterboxd, IMDb, MyAnimeList or Trakt — with your ratings and dates.", pill("Import", "/import")),
	step(3, "Hear about releases", "The bell collects release days, new episodes and new issues. Turn on phone notifications to get them anywhere.", pill("Notifications", "/account")),
	el("div", { style: { display: "flex", justifyContent: "flex-end", marginTop: "6px" } },
		el("button", {
			type: "button",
			onclick: done,
			style: { background: "none", border: "0", color: MAG.dim, fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "11px", cursor: "pointer", textTransform: "uppercase", letterSpacing: ".06em" },
		}, "Got it — hide this")));
}
